import haptic from '@/common/utils/haptics';
import merge from 'lodash/merge';
import { useSearchRouter } from './useSearchRouter';
import { useMemo } from 'react';
import { useSearch } from './useSearch';
import { useSearchRouting } from './useSearchRouting';
import { areaResetHref } from '../functions/areaPages';

export const useFilterChange = () => {
  const {
    query: { params, ...queries },
    ...router
  } = useSearchRouter();
  const { selectedFilters, filters: backendFilters } = useSearch();
  const { changeRoute } = useSearchRouting();

  const filters = useMemo(() => merge(queries, selectedFilters), [router.asPath, selectedFilters]);

  // DR-43: the neighbourhood is a PATH filter: choosing one is a link (its item's href), leaving it is the group's reset_href
  // (the parent listing with the same query) — not a query key to delete.
  const goTo = (href: string) => {
    haptic('selection');
    router.push(href, undefined, { shallow: true, scroll: true });
  };

  // `area` is merged into `filters` from selected_filters (so the radio shows it checked) but lives in the path, never in the query
  const withoutArea = (q: Record<string, any>) => {
    const { area: _area, ...rest } = q;
    return rest;
  };

  const removeFilter = (name: string) => {
    if (name === 'area') {
      const reset = areaResetHref(backendFilters);
      if (reset) return goTo(reset);
    }
    haptic('selection');
    delete queries[name];
    delete queries.page;
    // no reset_href from the API: leaving the area still means dropping the path segment — naming the specialty makes
    // changeRoute build /s/<city>/<specialty>/ (it keeps the area only while city and specialty are untouched)
    const slug = params as string[] | undefined;
    const leaveArea = name === 'area' && Array.isArray(slug) && slug.length === 3 && !!slug[1];
    // (`filters` merged selected_filters — city/category/area, all path parts — into `queries`; none belongs in the new query)
    const { city: _city, category: _category, ...rest } = withoutArea(queries);
    changeRoute({
      ...(leaveArea && { params: { category: slug[1] } }),
      query: leaveArea ? rest : withoutArea(queries),
      previousQueries: false,
    });
  };

  const handleChange = (name: string, value: string | boolean | string[]) => {
    haptic('selection');
    delete queries.page;
    let newQueries;
    if (typeof value === 'boolean' && !value) {
      delete queries[name];
      newQueries = {
        ...queries,
      };
    } else {
      newQueries = {
        ...queries,
        [name]: value,
      };
    }
    changeRoute({
      query: withoutArea(newQueries),
      previousQueries: false,
    });
  };

  return { handleChange, filters, removeFilter, goTo };
};
